import { useState, useEffect } from "react";
import { useToast } from "@/contexts/ToastContext";
import { feedbackApi, FeedbackType } from "@/api/feedback";

interface FeedbackModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const FEEDBACK_TYPES: { value: FeedbackType; label: string; icon: string }[] = [
  { value: "general", label: "General", icon: "chat" },
  { value: "bug", label: "Bug", icon: "bug_report" },
  { value: "feature", label: "Feature", icon: "auto_awesome" },
];

const MAX_MESSAGE_LENGTH = 1000;
const MIN_MESSAGE_LENGTH = 10;

export function FeedbackModal({ isOpen, onClose }: FeedbackModalProps) {
  const { showToast } = useToast();
  const [feedbackType, setFeedbackType] = useState<FeedbackType>("general");
  const [message, setMessage] = useState("");
  const [rating, setRating] = useState<number | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setFeedbackType("general");
      setMessage("");
      setRating(null);
      setError(null);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const isMessageValid =
    message.trim().length >= MIN_MESSAGE_LENGTH &&
    message.trim().length <= MAX_MESSAGE_LENGTH;

  const handleMessageChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    if (value.length <= MAX_MESSAGE_LENGTH) {
      setMessage(value);
      setError(null);
    }
  };

  const handleRatingClick = (value: number) => {
    setRating(rating === value ? null : value);
  };

  const handleSubmit = async () => {
    if (!isMessageValid || isSubmitting) return;

    setIsSubmitting(true);
    setError(null);

    try {
      await feedbackApi.createFeedback({
        type: feedbackType,
        message: message.trim(),
        ...(rating !== null && { rating }),
      });
      showToast("Thanks for your feedback!", "success");
      onClose();
    } catch (err: any) {
      console.error("Failed to submit feedback:", err);
      const errorMessage =
        err.response?.data?.message ||
        "Failed to submit feedback. Please try again.";
      setError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop with blur */}
      <div
        className="absolute inset-0 bg-[#051e12]/80 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative w-full max-w-md bg-[#0f291e] rounded-3xl shadow-2xl border border-[#2d4a3e] overflow-hidden z-10">
        {/* Close Button */}
        <button
          onClick={onClose}
          disabled={isSubmitting}
          className="absolute top-4 right-4 p-2 rounded-full text-slate-400 hover:bg-white/5 transition-colors disabled:opacity-50"
        >
          <span className="material-symbols-outlined text-xl">close</span>
        </button>

        {/* Content */}
        <div className="p-6 sm:p-8 flex flex-col items-center">
          {/* Icon */}
          <div className="w-16 h-16 bg-[#22c55e]/20 rounded-2xl flex items-center justify-center mb-4 ring-1 ring-[#22c55e]/30 shadow-[0_0_10px_rgba(34,197,94,0.2)]">
            <span className="material-symbols-outlined text-3xl text-[#22c55e]">
              chat_bubble
            </span>
          </div>

          {/* Header */}
          <h2 className="text-2xl font-bold text-white mb-2 text-center">
            Send Feedback
          </h2>
          <p className="text-slate-400 text-sm text-center mb-8 font-medium">
            Help us improve your experience
          </p>

          {/* Type Selector */}
          <div className="w-full mb-6">
            <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-3 ml-1">
              Type
            </label>
            <div className="grid grid-cols-3 gap-3">
              {FEEDBACK_TYPES.map((type) => (
                <button
                  key={type.value}
                  onClick={() => setFeedbackType(type.value)}
                  className={`group flex items-center justify-center gap-2 py-2.5 px-2 rounded-xl transition-all active:scale-95 ${
                    feedbackType === type.value
                      ? "bg-[#22c55e] text-white"
                      : "bg-[#1a382a] text-slate-400 border border-transparent hover:border-[#2d4a3e] hover:text-white"
                  }`}
                >
                  <span className="material-symbols-outlined text-lg">
                    {type.icon}
                  </span>
                  <span className={`text-sm ${feedbackType === type.value ? "font-semibold" : "font-medium"}`}>
                    {type.label}
                  </span>
                </button>
              ))}
            </div>
          </div>

          {/* Message Field */}
          <div className="w-full mb-6">
            <div className="flex justify-between items-center mb-2 ml-1">
              <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider">
                Message
              </label>
              <span className="text-xs text-slate-400 opacity-70">
                {message.length}/{MAX_MESSAGE_LENGTH}
              </span>
            </div>
            <textarea
              className="w-full h-32 bg-[#1a382a] border border-[#2d4a3e] rounded-2xl p-4 text-sm text-slate-200 placeholder-slate-500 focus:ring-2 focus:ring-[#22c55e]/50 focus:border-[#22c55e] outline-none resize-none transition-all"
              placeholder="Tell us what's on your mind..."
              value={message}
              onChange={handleMessageChange}
            />
            <p className="text-xs text-slate-400 mt-2 ml-1 opacity-70">
              Minimum {MIN_MESSAGE_LENGTH} characters
            </p>
          </div>

          {/* Rating */}
          <div className="w-full mb-8">
            <div className="flex items-center gap-2 mb-3 ml-1">
              <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider">
                Rate Your Experience
              </label>
              <span className="text-xs text-slate-400 font-normal lowercase">
                (optional)
              </span>
            </div>
            <div className="flex gap-2">
              {[1, 2, 3, 4, 5].map((value) => {
                const isActive = rating !== null && rating >= value;
                return (
                  <button
                    key={value}
                    onClick={() => handleRatingClick(value)}
                    className={`w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center rounded-xl transition-transform hover:scale-105 active:scale-95 ${
                      isActive
                        ? "bg-[#eab308]/10 border border-[#eab308]/50 text-[#eab308] shadow-[0_0_10px_rgba(234,179,8,0.2)]"
                        : "bg-[#1a382a] text-slate-500/40 border border-transparent hover:border-slate-500/20 hover:text-slate-500"
                    }`}
                  >
                    <span
                      className="material-symbols-outlined text-2xl"
                      style={{
                        fontVariationSettings: isActive ? "'FILL' 1" : "'FILL' 0"
                      }}
                    >
                      star
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className="w-full flex gap-2 rounded-xl bg-red-500/10 border border-red-500/20 p-3 mb-4">
              <span className="material-symbols-outlined text-red-400 text-lg shrink-0">
                error
              </span>
              <p className="text-red-300 text-sm">{error}</p>
            </div>
          )}

          {/* Submit Button */}
          <button
            onClick={handleSubmit}
            disabled={!isMessageValid || isSubmitting}
            className="w-full bg-[#22c55e] hover:bg-[#16a34a] text-white py-3.5 rounded-xl font-bold text-base shadow-[0_0_15px_rgba(34,197,94,0.3)] flex items-center justify-center gap-2 transition-all transform hover:scale-[1.02] active:scale-[0.98] disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:scale-100 disabled:shadow-none"
          >
            {isSubmitting ? (
              <>
                <span className="material-symbols-outlined animate-spin">
                  progress_activity
                </span>
                Submitting...
              </>
            ) : (
              <>
                <span className="material-symbols-outlined">send</span>
                Submit Feedback
              </>
            )}
          </button>

          {/* Cancel Button */}
          <button
            onClick={onClose}
            disabled={isSubmitting}
            className="mt-4 text-sm font-medium text-slate-400 hover:text-white transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
